import Moment from "moment";
import { compareDates } from "@/utils/helpers";
import { first, last, split } from "lodash";
import { extendMoment } from "moment-range";
import {
  API_DATE_FORMAT,
  BASE_DATE_FORMAT,
  DATE_FORMAT_WITH_SEPARATOR_REVERSED
} from "@/constants/common";

const moment = extendMoment(Moment);

export const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";

export const getDurationBetween = (startTime, endTime, format = "HH:mm") => {
  const duration = compareDates(endTime, startTime, format);
  return duration < 0 ? moment.duration(0) : moment.duration(duration);
};

export const formatDateForVCalendar = dateInstance => {
  return (
    dateInstance.getFullYear() +
    "-" +
    (dateInstance.getMonth() + 1).toString().padStart(2, "0") +
    "-" +
    dateInstance.getDate()
  );
};

export const formatDate = ({
  date,
  startFormat = API_DATE_FORMAT,
  endFormat = DATE_FORMAT_WITH_SEPARATOR_REVERSED
}) => (date ? moment(date, startFormat).format(endFormat) : "");

export const getSequencesOfDates = dates => {
  if (!dates.length) {
    return [];
  }

  const sortedDates = [...dates].sort((a, b) => a.valueOf() - b.valueOf());
  const dateSequences = [];
  let currentSequence = [first(sortedDates)];

  sortedDates.forEach(date => {
    const lastDate = last(currentSequence);
    if (areContiguousDates(date, lastDate)) {
      currentSequence.push(date);
    } else {
      dateSequences.push({
        start: first(currentSequence),
        end: last(currentSequence)
      });
      currentSequence = [date];
    }
  });

  if (currentSequence.length) {
    dateSequences.push({
      start: first(currentSequence),
      end: last(currentSequence)
    });
  }

  return dateSequences;
};

export const areContiguousDates = (firstDate, secondDate) =>
  Math.abs(moment(firstDate).diff(moment(secondDate), "days")) <= 1;

export const splitHourAndMinutes = time => split(time, ":");

export const setHoursAndMinutes = (date, { hours, minutes }) => {
  const dateToChange = moment(date);
  dateToChange.hours(hours);
  dateToChange.minutes(minutes);
  return dateToChange.format(DATE_TIME_FORMAT);
};
export const getDayOfWeek = date => moment(date, API_DATE_FORMAT).format("ddd");
export const getDayOfMonth = date => moment(date, API_DATE_FORMAT).format("DD");
export const getMonth = date => moment(date, BASE_DATE_FORMAT).format("MMM");

export const DAY_MONTH_FORMAT = "ddd Do MMM";
export const FULL_DAY_MONTH_FORMAT = "dddd Do MMMM";
export const WEEKDAYS = [
  " Mon",
  " Tue",
  " Wed",
  " Thu",
  " Fri",
  " Sat",
  " Sun"
];

export const formatRangeDate = (
  date,
  format = "YYYY-MM-DD",
  currentFormat = BASE_DATE_FORMAT
) => (date ? moment(date, currentFormat).format(format) : date);

export const isValidDateRange = dateCover => {
  const isStartDateValid =
    dateCover[0] && moment(dateCover[0], "YYYY-MM-DD").isValid();
  const isEndDateValid =
    dateCover[1] === "Present"
      ? true
      : dateCover[1] && moment(dateCover[1], "YYYY-MM-DD").isValid();
  return isStartDateValid && isEndDateValid;
};

export const isValidDateString = (date, format = BASE_DATE_FORMAT) =>
  moment(date, format, true).isValid();

export const formatDateText = date =>
  date ? formatDate({ date, endFormat: BASE_DATE_FORMAT }) : "";
export const splitDateRange = dates => {
  const dateCovered = dates.split("-");
  const startDate = formatRangeDate(dateCovered[0]);
  const endDate = formatRangeDate(dateCovered[1]);
  return { dateCovered, startDate, endDate };
};

export const isDateInTheFuture = dateToCheck => {
  const currentDate = new Date().toISOString();
  return moment(currentDate).isBefore(moment(dateToCheck));
};

export const getDateRange = ({ startDate, endDate, format }) => {
  const start = formatDate({
    date: startDate,
    startFormat: API_DATE_FORMAT,
    endFormat: format
  });
  const end =
    endDate === "Present"
      ? endDate
      : formatDate({
          date: endDate,
          startFormat: API_DATE_FORMAT,
          endFormat: format
        });

  return `${start} - ${end}`;
};

export const getFullDateFormat = date =>
  moment(date).format("dddd Do MMMM, YYYY");

export const getTextDate = date => moment(date).format("Do MMMM YYYY");

export const isToday = date => {
  return moment().isSame(date, "day");
};

export const dateForHuman = date => {
  return isToday(date) ? "Today" : moment(date).fromNow();
};

export const isAfterDate = ({ startDate, endDate, format }) => {
  const momentStartDate = moment(startDate, format).format("YYYY-MM-DD");
  const momentEndDate = moment(endDate, format).format("YYYY-MM-DD");
  return moment(momentStartDate).isBefore(moment(momentEndDate));
};

export const getReportFormatDate = date => moment(date).format("DD/MM/YYYY");
